import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { FileText, Upload, CheckCircle, Database } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

function CursorIcon() {
  return (
    <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
      <path d="M2 2L18 12L10 14L6 22L2 2Z" fill="url(#cursorGradient)" stroke="rgba(96, 165, 250, 0.8)" strokeWidth="1" />
      <defs>
        <linearGradient id="cursorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgb(96, 165, 250)" />
          <stop offset="100%" stopColor="rgb(34, 211, 238)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function UploadArea({ onAnimationComplete }: { onAnimationComplete: () => void }) {
  const { t } = useLanguage();
  const [animationPhase, setAnimationPhase] = useState(0);
  
  useEffect(() => {
    const timer1 = setTimeout(() => setAnimationPhase(1), 400);
    const timer2 = setTimeout(() => setAnimationPhase(2), 800);
    const timer3 = setTimeout(() => setAnimationPhase(3), 1200);
    const timer4 = setTimeout(() => { setAnimationPhase(4); onAnimationComplete(); }, 1600);
    return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); clearTimeout(timer4); };
  }, [onAnimationComplete]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative h-[180px]">
      <motion.div
        animate={{ borderColor: animationPhase >= 3 ? "rgba(34, 211, 238, 0.6)" : "rgba(71, 85, 105, 0.5)", backgroundColor: animationPhase >= 3 ? "rgba(34, 211, 238, 0.05)" : "rgba(30, 41, 59, 0.3)" }}
        className="absolute inset-0 border-2 border-dashed rounded-xl flex flex-col items-center justify-center transition-colors"
      >
        <Upload className="w-6 h-6 text-slate-500 mb-2" />
        <p className="text-[10px] text-slate-400 text-center px-4">{t("ai.ocrCard.upload")}</p>
        <p className="text-[9px] text-slate-500 mt-0.5">{t("ai.ocrCard.uploadSub")}</p>
      </motion.div>
      
      <motion.div
        initial={{ x: -40, y: 120, opacity: 0 }}
        animate={{ x: animationPhase >= 3 ? 80 : animationPhase >= 2 ? -20 : -40, y: animationPhase >= 3 ? 70 : animationPhase >= 2 ? 100 : 120, opacity: animationPhase >= 1 ? 1 : 0, scale: animationPhase >= 3 ? 0.9 : 1 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="absolute z-10"
      >
        <div className="flex flex-col items-center gap-0.5 p-2 bg-slate-700/90 rounded-lg border border-cyan-500/40 shadow-lg">
          <FileText className="w-5 h-5 text-cyan-400" />
          <span className="text-[8px] text-cyan-300">{t("ai.ocrCard.file")}</span>
        </div>
      </motion.div>
      
      <motion.div
        initial={{ x: -60, y: 140, opacity: 0 }}
        animate={{ x: animationPhase >= 3 ? 100 : animationPhase >= 1 ? -15 : -60, y: animationPhase >= 3 ? 90 : animationPhase >= 1 ? 115 : 140, opacity: animationPhase >= 1 && animationPhase < 4 ? 1 : 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="absolute z-20"
      >
        <CursorIcon />
      </motion.div>
    </motion.div>
  );
}

function DocumentScanner({ scanProgress, highlightedFields, isArchived }: { scanProgress: number; highlightedFields: string[]; isArchived: boolean }) {
  const { t } = useLanguage();
  
  const documentFields = [
    { id: "name", label: t("ai.ocrCard.field1"), value: t("ai.ocrCard.field1Val"), top: 25 },
    { id: "hs", label: t("ai.ocrCard.field2"), value: t("ai.ocrCard.field2Val"), top: 40 },
    { id: "weight", label: t("ai.ocrCard.field3"), value: t("ai.ocrCard.field3Val"), top: 55 },
    { id: "port", label: t("ai.ocrCard.field4"), value: t("ai.ocrCard.field4Val"), top: 70 },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: isArchived ? 0.5 : 1, y: 0 }} className="relative w-28 h-36 bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-2 space-y-1.5">
        <div className="h-3 w-16 bg-slate-200 rounded" />
        <div className="h-2 w-full bg-slate-100 rounded" />
        
        {documentFields.map((field) => (
          <motion.div key={field.id} className="relative" style={{ marginTop: 4 }}>
            <div className="h-2 w-20 bg-slate-100 rounded" />
            <AnimatePresence>
              {highlightedFields.includes(field.id) && !isArchived && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 border-2 border-cyan-400 rounded bg-cyan-400/10" style={{ boxShadow: "0 0 8px rgba(34, 211, 238, 0.5)" }} />
              )}
            </AnimatePresence>
          </motion.div>
        ))}
        
        <div className="h-2 w-14 bg-slate-100 rounded mt-2" />
        <div className="h-2 w-18 bg-slate-100 rounded" />
      </div>
      
      {scanProgress > 0 && scanProgress < 100 && !isArchived && (
        <motion.div className="absolute left-0 right-0 h-1 bg-gradient-to-b from-cyan-400/80 via-cyan-300 to-cyan-400/80" style={{ top: `${scanProgress}%`, boxShadow: "0 0 20px rgba(34, 211, 238, 0.8), 0 0 40px rgba(34, 211, 238, 0.4)" }} />
      )}
      
      {isArchived && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 flex items-center justify-center bg-slate-900/30">
          <span className="px-2 py-1 bg-slate-600/80 rounded text-[8px] text-slate-300">{t("ai.ocrCard.archived")}</span>
        </motion.div>
      )}
    </motion.div>
  );
}

function ExtractedField({ label, value, isActive, delay }: { label: string; value: string; isActive: boolean; delay: number }) {
  const { t } = useLanguage();
  const [showBubble, setShowBubble] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setShowBubble(true), delay * 1000 + 300);
      const hideTimer = setTimeout(() => setShowBubble(false), delay * 1000 + 1500);
      return () => { clearTimeout(timer); clearTimeout(hideTimer); };
    }
    return undefined;
  }, [isActive, delay]);

  return (
    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: isActive ? 1 : 0.3, x: isActive ? 0 : -20 }} transition={{ delay: delay, duration: 0.4 }} className="relative flex items-center gap-2 p-1.5 bg-slate-700/50 rounded border border-slate-600/50">
      <span className="text-[9px] text-slate-400 w-12">{label}</span>
      <motion.span initial={{ opacity: 0 }} animate={{ opacity: isActive ? 1 : 0 }} transition={{ delay: delay + 0.2 }} className="text-[10px] text-cyan-300 font-medium">{value}</motion.span>
      
      <AnimatePresence>
        {showBubble && (
          <motion.div initial={{ opacity: 0, scale: 0.8, y: 5 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8 }} className="absolute -top-5 right-0 px-1.5 py-0.5 bg-cyan-500/20 rounded text-[8px] text-cyan-300 border border-cyan-400/30">
            {t("ai.ocrCard.extracted")}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function AccuracyCounter({ isActive }: { isActive: boolean }) {
  const { t } = useLanguage();
  const [accuracy, setAccuracy] = useState(0);

  useEffect(() => {
    if (!isActive) { setAccuracy(0); return; }
    const interval = setInterval(() => {
      setAccuracy(prev => { if (prev >= 99.2) { clearInterval(interval); return 99.2; } return Math.min(prev + 4.5, 99.2); });
    }, 50);
    return () => clearInterval(interval);
  }, [isActive]);

  if (!isActive && accuracy === 0) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-1 mt-2">
      <CheckCircle className="w-3 h-3 text-emerald-400" />
      <span className="text-[10px] text-slate-400">{t("ai.ocrCard.accuracy")}</span>
      <span className="text-[11px] text-emerald-400 font-mono font-bold">{accuracy.toFixed(1)}%</span>
    </motion.div>
  );
}

function SyncIndicator({ isActive }: { isActive: boolean }) {
  const { t } = useLanguage();
  if (!isActive) return null;

  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="px-2 py-1 bg-violet-500/20 rounded text-[8px] text-violet-300 border border-violet-400/30 whitespace-nowrap">
      {t("ai.ocrCard.synced")}
    </motion.div>
  );
}

export function OCRDocumentCard() {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);
  const [highlightedFields, setHighlightedFields] = useState<string[]>([]);
  const [scanProgress, setScanProgress] = useState(0);
  const [showFinalGlow, setShowFinalGlow] = useState(false);

  const documentFields = [
    { id: "name", label: t("ai.ocrCard.field1"), value: t("ai.ocrCard.field1Val") },
    { id: "hs", label: t("ai.ocrCard.field2"), value: t("ai.ocrCard.field2Val") },
    { id: "weight", label: t("ai.ocrCard.field3"), value: t("ai.ocrCard.field3Val") },
    { id: "port", label: t("ai.ocrCard.field4"), value: t("ai.ocrCard.field4Val") },
  ];
  
  const handleDrop = () => setPhase(1);

  useEffect(() => {
    if (phase === 0) return;
    if (phase === 1) { const timer = setTimeout(() => setPhase(2), 400); return () => clearTimeout(timer); }
    if (phase === 2) {
      let progress = 0;
      const scanInterval = setInterval(() => {
        progress += 4;
        setScanProgress(progress);
        if (progress >= 25 && !highlightedFields.includes("name")) setHighlightedFields(prev => [...prev, "name"]);
        if (progress >= 40 && !highlightedFields.includes("hs")) setHighlightedFields(prev => [...prev, "hs"]);
        if (progress >= 55 && !highlightedFields.includes("weight")) setHighlightedFields(prev => [...prev, "weight"]);
        if (progress >= 70 && !highlightedFields.includes("port")) setHighlightedFields(prev => [...prev, "port"]);
        if (progress >= 100) { clearInterval(scanInterval); setTimeout(() => setPhase(3), 300); }
      }, 30);
      return () => clearInterval(scanInterval);
    }
    if (phase === 3) { const timer = setTimeout(() => setPhase(4), 1500); return () => clearTimeout(timer); }
    if (phase === 4) { const timer = setTimeout(() => { setPhase(5); setShowFinalGlow(true); setTimeout(() => setShowFinalGlow(false), 800); }, 2000); return () => clearTimeout(timer); }
    if (phase === 5) { const timer = setTimeout(() => { setPhase(0); setHighlightedFields([]); setScanProgress(0); }, 3000); return () => clearTimeout(timer); }
    return undefined;
  }, [phase, highlightedFields]);

  return (
    <div className={`relative min-h-[320px] rounded-2xl bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 backdrop-blur-xl border border-slate-700/50 overflow-hidden transition-all duration-500 ${showFinalGlow ? "shadow-[0_0_40px_rgba(167,139,250,0.3)]" : ""}`}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(34, 211, 238, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.2) 1px, transparent 1px)`, backgroundSize: '20px 20px' }} />
      </div>

      <div className="relative z-10 p-4 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
            <h3 className="text-sm font-semibold text-slate-100">{t("ai.ocrCard.title")}</h3>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-violet-500/10 rounded border border-violet-500/20">
            <Database className="w-3 h-3 text-violet-400" />
            <span className="text-[9px] text-violet-300">{t("ai.ocrCard.system")}</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 p-4">
        {phase === 0 ? (
          <UploadArea onAnimationComplete={handleDrop} />
        ) : (
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <DocumentScanner scanProgress={scanProgress} highlightedFields={highlightedFields} isArchived={phase === 5} />
            </div>
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[9px] text-slate-500">{t("ai.ocrCard.structure")}</p>
                <AnimatePresence><SyncIndicator isActive={phase >= 4} /></AnimatePresence>
              </div>
              {documentFields.map((field, idx) => (
                <ExtractedField key={field.id} label={field.label} value={field.value} isActive={phase >= 3} delay={idx * 0.3} />
              ))}
              <AccuracyCounter isActive={phase >= 3} />
            </div>
          </div>
        )}
      </div>

      <motion.div className="absolute bottom-3 left-0 right-0 flex justify-center">
        <div className={`px-3 py-1.5 rounded-full backdrop-blur-xs border text-[10px] transition-all duration-300 ${phase === 5 ? "bg-violet-500/20 border-violet-500/30 text-violet-300" : phase >= 2 ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-300" : "bg-slate-800/50 border-slate-700/50 text-slate-400"}`}>
          {phase === 0 && t("ai.ocrCard.wait")}
          {phase === 1 && t("ai.ocrCard.loading")}
          {phase === 2 && t("ai.ocrCard.recognizing")}
          {phase === 3 && t("ai.ocrCard.extracting")}
          {phase === 4 && t("ai.ocrCard.syncing")}
          {phase === 5 && <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />{t("ai.ocrCard.complete")}</span>}
        </div>
      </motion.div>
    </div>
  );
}
